/**
 * ZAXIA Demo Mode Provider
 * Phase D4.5.5: Investor Demo Orchestration & Recovery
 * 
 * This is the SINGLE SOURCE OF TRUTH for Demo Mode.
 * When DEMO_MODE is ON:
 * - Demo-only behaviors are enabled
 * - Recovery commands work ("reset demo", "restart session", "clear all bookings")
 * - Flow timeouts auto-recover
 * - Concierge tone is softer (no "error", "invalid", "failed")
 * 
 * Demo mode does NOT affect production logic.
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

// =============================================================================
// TYPES
// =============================================================================

interface DemoModeContextValue {
  /** Whether demo mode is enabled */
  isDemoMode: boolean;
  /** Toggle demo mode on/off */
  toggleDemoMode: () => void;
  /** Enable demo mode */
  enableDemoMode: () => void;
  /** Disable demo mode */
  disableDemoMode: () => void;
  
  // Flow timeout tracking
  /** Timestamp when a pending flow started (for timeout tracking) */
  pendingFlowStartTime: number | null;
  /** Type of pending flow */
  pendingFlowType: 'modify' | 'cancel' | 'explore-transport' | 'spa-booking' | null;
  /** Start tracking a pending flow */
  startPendingFlow: (flowType: 'modify' | 'cancel' | 'explore-transport' | 'spa-booking') => void;
  /** Clear pending flow tracking */
  clearPendingFlow: () => void;
  
  // Demo tone helpers
  /** Transform technical error messages to friendly concierge language */
  toFriendlyMessage: (technicalMessage: string) => string;
  
  // Repeated failure tracking
  /** Number of consecutive voice recognition failures */
  consecutiveFailures: number;
  /** Increment failure count */
  incrementFailures: () => void;
  /** Reset failure count */
  resetFailures: () => void;
}

const DemoModeContext = createContext<DemoModeContextValue | null>(null);

// =============================================================================
// DEMO TONE TRANSFORMATIONS
// Map technical language to friendly concierge language
// =============================================================================

const FRIENDLY_MESSAGES: Record<string, string> = {
  // Error states
  'error': "Something didn't work as expected",
  'invalid': "That doesn't seem quite right",
  'failed': "I couldn't complete that",
  'not found': "I couldn't find that",
  'undefined': "Something went wrong",
  'null': "Something went wrong",
  'exception': "I ran into a small issue",
  'timeout': "That took a bit too long",
  
  // Operation states
  'invalid operation': "That doesn't seem possible right now — let me take you back",
  'operation failed': "I couldn't do that right now — shall we try something else?",
  'cannot modify': "That booking can't be changed right now",
  'cannot cancel': "That booking can't be cancelled right now",
  'booking not found': "I couldn't find that booking — let me show you what's available",
  'no bookings': "You don't have any bookings yet — would you like to make one?",
  'no results': "I couldn't find anything matching that",
  
  // Network/system states
  'network error': "I'm having trouble connecting right now",
  'server error': "The system is a bit busy — let's try again",
  'connection failed': "I couldn't reach the service right now",
};

function transformToFriendlyMessage(technicalMessage: string): string {
  const lower = technicalMessage.toLowerCase();
  
  // Check for exact matches first
  for (const [technical, friendly] of Object.entries(FRIENDLY_MESSAGES)) {
    if (lower === technical) {
      return friendly;
    }
  }
  
  // Check for partial matches
  for (const [technical, friendly] of Object.entries(FRIENDLY_MESSAGES)) {
    if (lower.includes(technical)) {
      return friendly;
    }
  }
  
  // Default transformation: remove technical words
  let result = technicalMessage;
  const bannedWords = ['error', 'invalid', 'failed', 'exception', 'undefined', 'null'];
  for (const word of bannedWords) {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    result = result.replace(regex, '');
  }
  
  // If the message became empty or too short, use a generic friendly message
  if (result.trim().length < 5) {
    return "I ran into a small issue — let me help you with something else.";
  }
  
  return result.trim();
}

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

interface DemoModeProviderProps {
  children: ReactNode;
  /** Initial demo mode state (default: check localStorage or env) */
  initialDemoMode?: boolean;
}

export function DemoModeProvider({ children, initialDemoMode }: DemoModeProviderProps) {
  // Initialize from localStorage, env, or prop
  const [isDemoMode, setIsDemoMode] = useState(() => {
    if (initialDemoMode !== undefined) return initialDemoMode;
    
    // Check localStorage
    const stored = localStorage.getItem('ZAXIA_DEMO_MODE');
    if (stored !== null) return stored === 'true';
    
    // Check environment variable (Vite style)
    if (import.meta.env.VITE_DEMO_MODE === 'true') return true;
    
    // Default: enabled for development
    return import.meta.env.DEV;
  });
  
  // Flow timeout tracking
  const [pendingFlowStartTime, setPendingFlowStartTime] = useState<number | null>(null);
  const [pendingFlowType, setPendingFlowType] = useState<'modify' | 'cancel' | 'explore-transport' | 'spa-booking' | null>(null);
  
  // Repeated failure tracking
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  
  // Persist demo mode to localStorage
  useEffect(() => {
    localStorage.setItem('ZAXIA_DEMO_MODE', String(isDemoMode));
    console.log(`[ZAXIA Demo] Demo mode: ${isDemoMode ? 'ON' : 'OFF'}`);
  }, [isDemoMode]);
  
  const toggleDemoMode = useCallback(() => {
    setIsDemoMode(prev => !prev);
  }, []);
  
  const enableDemoMode = useCallback(() => {
    setIsDemoMode(true);
  }, []);
  
  const disableDemoMode = useCallback(() => {
    setIsDemoMode(false);
  }, []);
  
  const startPendingFlow = useCallback((flowType: 'modify' | 'cancel' | 'explore-transport' | 'spa-booking') => {
    setPendingFlowStartTime(Date.now());
    setPendingFlowType(flowType);
    console.log(`[ZAXIA Demo] Started pending flow: ${flowType}`);
  }, []);
  
  const clearPendingFlow = useCallback(() => {
    if (pendingFlowType) {
      console.log(`[ZAXIA Demo] Cleared pending flow: ${pendingFlowType}`);
    }
    setPendingFlowStartTime(null);
    setPendingFlowType(null);
  }, [pendingFlowType]);
  
  const toFriendlyMessage = useCallback((technicalMessage: string) => {
    if (!isDemoMode) return technicalMessage;
    return transformToFriendlyMessage(technicalMessage);
  }, [isDemoMode]);
  
  const incrementFailures = useCallback(() => {
    setConsecutiveFailures(prev => prev + 1);
  }, []);
  
  const resetFailures = useCallback(() => {
    setConsecutiveFailures(0);
  }, []);
  
  const value: DemoModeContextValue = {
    isDemoMode,
    toggleDemoMode,
    enableDemoMode,
    disableDemoMode,
    pendingFlowStartTime,
    pendingFlowType,
    startPendingFlow,
    clearPendingFlow,
    toFriendlyMessage,
    consecutiveFailures,
    incrementFailures,
    resetFailures,
  };
  
  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useDemoMode(): DemoModeContextValue {
  const context = useContext(DemoModeContext);
  if (!context) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}

/**
 * Hook to manage flow timeouts in demo mode.
 * Automatically navigates back or shows recovery prompt if a flow takes too long.
 */
export function useFlowTimeout(onTimeout: () => void) {
  const { isDemoMode, pendingFlowStartTime, pendingFlowType, clearPendingFlow } = useDemoMode();
  
  useEffect(() => {
    if (!isDemoMode || !pendingFlowStartTime || !pendingFlowType) {
      return;
    }
    
    const elapsed = Date.now() - pendingFlowStartTime;
    const remaining = FLOW_TIMEOUT_MS - elapsed;
    
    if (remaining <= 0) {
      // Already timed out
      console.log(`[ZAXIA Demo] Flow ${pendingFlowType} already timed out`);
      clearPendingFlow();
      onTimeout();
      return;
    }
    
    const timer = setTimeout(() => {
      console.log(`[ZAXIA Demo] Flow ${pendingFlowType} timed out after ${FLOW_TIMEOUT_MS}ms`);
      clearPendingFlow();
      onTimeout();
    }, remaining);
    
    return () => clearTimeout(timer);
  }, [isDemoMode, pendingFlowStartTime, pendingFlowType, clearPendingFlow, onTimeout]);
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Flow timeout in milliseconds (15 seconds) */
export const FLOW_TIMEOUT_MS = 15000;

/** Long silence timeout in milliseconds (6 seconds) */
export const SILENCE_TIMEOUT_MS = 6000;

/** Number of failures before suggesting remote fallback */
export const MAX_CONSECUTIVE_FAILURES = 3;
